/**
 * drawingMethods.ts
 * ドローイング（描画）関連メソッド
 * App.vue から切り出し
 */

import {
  addDraw,
  animateRelocate,
  isPdfFile,
} from '@/js/downLoad'
import {
  geojsonUpdate,
} from '@/js/pyramid'
import {
  clickCircleSource,
} from '@/js/layers'
import {
  drawCancel,
  drawConfirm,
  removeLastVertex,
  toggleDrawMode,
  toggleDrawFix as toggleDrawFixCore,
} from '@/js/draw'

type VM = any

// DrawToolクラスのセットアップ
export function drawToolSet(vm: VM): void {
  class DrawTool {
    map: any
    minPolygonPoints: number
    minLinePoints: number
    floatingGroup: HTMLElement | null
    confirmButton: HTMLElement | null
    undoButton: HTMLElement | null
    cancelButton: HTMLElement | null

    constructor(map: any) {
      this.map = map;
      this.minPolygonPoints = 3
      this.minLinePoints = 2

      // フローティングボタン要素（コメントアウト済みの場合はnull）
      this.floatingGroup = document.getElementById('floating-buttons');
      this.confirmButton = document.getElementById('confirm-btn');
      this.undoButton = document.getElementById('undo-btn');
      this.cancelButton = document.getElementById('cancel-btn');

      // イベントバインド
      this.map.on('click', this.handleClick.bind(this));
      this.map.on('mousemove', this.handleMouseMove.bind(this));

      // ボタンイベント（要素が存在する場合のみ）
      if (this.confirmButton) this.confirmButton.addEventListener('click', this.handleConfirm.bind(this));
      if (this.undoButton) this.undoButton.addEventListener('click', this.handleUndo.bind(this));
      if (this.cancelButton) this.cancelButton.addEventListener('click', this.handleCancel.bind(this));
    }

    handleClick(e: any) {
      vm.cancelBtnMiniTooltip = '描きかけ中の全ポイントを削除'
      vm.undoBtnMiniTooltip = '直前のポイントを削除'
      vm.isUndoBtnDisabled = false
      if (!vm.s_isDrawLine && !vm.s_isDrawPolygon) return;
      const lng = e.lngLat.lng;
      const lat = e.lngLat.lat;
      vm.tempLineCoordsGuide.push([lng, lat]);
      vm.isDrawingLine = true;
      vm.confirmBtnColor = 'info'
      // UI位置追従: 最後のポイントにボタンを移動
      if (vm.tempLineCoordsGuide.length >= 2) {
        this.updateFloatingPosition(e.lngLat);
      }
    }

    handleMouseMove(_e: any) {
    }

    updateFloatingPosition(lngLat: any) {
      // フローティングボタンが存在しない場合はスキップ
      if (!this.floatingGroup) return

      // ユーザーが手動で移動した位置があればそれを維持
      if (vm.floatingButtonsUserPos) {
        vm.$store.state.showDrawConfrim = true
        return
      }

      // マップ座標を画面ピクセルに変換
      const map01Element = document.querySelector('#map01') as HTMLElement
      const map01Width = map01Element.clientWidth
      const map01Height = map01Element.clientHeight
      const pixel = this.map.project(lngLat);
      const panelW = this.floatingGroup.offsetWidth
      const panelH = this.floatingGroup.offsetHeight
      const M = 10; // マージン

      // 画面左半分: 右に余白 +M / 右半分: パネル幅ぶん左へ引く
      let left = (pixel.x <= map01Width / 2)
          ? pixel.x + M
          : pixel.x - panelW - M;
      let top = pixel.y + M;

      // 画面の境界チェック - 左右
      if (left < M) {
        left = M;
      } else if (left + panelW > map01Width - M) {
        left = map01Width - panelW - M;
      }

      // 画面の境界チェック - 上下
      if (top < M) {
        top = M;
      } else if (top + panelH > map01Height - M) {
        top = map01Height - panelH - M;
      }

      animateRelocate(this.floatingGroup, left, top, { duration: 200, easing: 'ease-out' });
      vm.$store.state.showDrawConfrim = true
    }

    handleConfirm() {
      drawConfirm()
    }

    handleUndo() {
      removeLastVertex()
    }

    handleCancel() {
      drawCancel()
    }

  }
  // DrawToolインスタンス化
  vm.drawTool = new DrawTool(vm.map01);
}

// ドローリストの開閉
export function drawListOpen(vm: VM): void {
  if (vm.showDrawListDrawer) {
    vm.$store.commit('setDrawListDrawer', false)
  } else {
    vm.$store.commit('setDrawListDrawer', true)
  }
}

// ドロー描画モードを開く
export function drawOpen(vm: VM): void {
  vm.$store.state.isDraw = true
  vm.$store.state.drawButtonsVisible = true
}

// ドローGeoJSONに画像を追加
export function drawGeojsonAddPicture(vm: VM): void {
  const map01 = vm.$store.state.map01
  if (!vm.$store.state.drawGeojsonId) {
    alert('必要な引数がたりません')
    return
  }
  const id = vm.$store.state.drawGeojsonId
  const tgtProp = 'pictureUrl'
  const borderRadius = vm.thumbnailType.borderRadius
  const pictureUrl = vm.s_pictureUrl
  const containerSize = vm.thumbnailType.containerSize
  const containerColor = vm.containerColor
  const value = {pictureUrl, borderRadius, containerSize, containerColor}
  vm.$store.state.clickCircleGeojsonText = geojsonUpdate(map01,null,clickCircleSource.iD,id,tgtProp,value)

  // PDFの場合、吹き出しを強制的に通常(0)にする
  if (pictureUrl && isPdfFile(pictureUrl)) {
    vm.$store.state.clickCircleGeojsonText = geojsonUpdate(map01, null, clickCircleSource.iD, id, 'labelType', '0')
    vm.$store.state.clickCircleGeojsonText = geojsonUpdate(map01, null, clickCircleSource.iD, id, 'balloonDirection', 'top')
  }
}

// GeoJSONファイルをアップロードしてドローに追加
export function uploadDraw(vm: VM): void {
  vm.dialogForChibanzyOrDraw = false

  // s_geojsonFileが有効なFileオブジェクトか確認
  if (!vm.s_geojsonFile || !(vm.s_geojsonFile instanceof File || vm.s_geojsonFile instanceof Blob)) {
    alert('ファイルが選択されていません');
    return;
  }

  const reader = new FileReader();
  reader.onload = (event: any) => {
    const geojson = JSON.parse(event.target.result)
    // GeoJSONからプロパティを抽出
    if (geojson.features && geojson.features.length > 0) {
      const firstFeature = geojson.features[0]
      if (firstFeature.properties) {
        vm.geojsonProperties = Object.keys(firstFeature.properties)
        vm.geojsonData = geojson
        // デフォルトで'name'や'title'があれば選択
        if (vm.geojsonProperties.includes('name')) {
          vm.geojsonLabelProperty = 'name'
        } else if (vm.geojsonProperties.includes('title')) {
          vm.geojsonLabelProperty = 'title'
        } else if (vm.geojsonProperties.length > 0) {
          vm.geojsonLabelProperty = vm.geojsonProperties[0]
        } else {
          vm.geojsonLabelProperty = ''
        }
        vm.dialogForDrawGeojson = true
      } else {
        // プロパティがない場合はそのまま追加
        addDraw(geojson, true, undefined)
      }
    } else {
      // フィーチャーがない場合はそのまま追加
      addDraw(geojson, true, undefined)
    }
  }
  reader.readAsText(vm.s_geojsonFile);
}

// GeoJSONアップロード後にラベル・色を設定してドローに追加
export function uploadDrawGeojson(vm: VM): void {
  vm.dialogForDrawGeojson = false
  const geojson = vm.geojsonData
  if (!geojson) return

  // geometryとpropertiesが有効なフィーチャーのみをフィルタリング
  geojson.features = geojson.features.filter((feature: any) => {
    return feature.geometry && feature.properties
  })

  // 選択されたパラメータを各フィーチャーに適用
  geojson.features.forEach((feature: any) => {
    const geoType = feature.geometry.type
    const props = feature.properties

    // ラベルの設定
    if (vm.geojsonLabelProperty && vm.geojsonLabelProperty !== '(なし)') {
      props.label = props[vm.geojsonLabelProperty] || ''
    } else {
      props.label = ''
    }

    // 色の設定
    if (geoType === 'Point') {
      props['point-color'] = vm.geojsonPointColor
    } else if (geoType === 'Polygon' || geoType === 'MultiPolygon') {
      props.color = 'rgba(0,0,255,0.1)'
    } else {
      props.color = 'black'
    }

    // その他の必須プロパティ
    props.offsetValue = [0.6, 0]
    props.textAnchor = 'left'
    props.textJustify = 'left'
  })

  addDraw(geojson, true, undefined)
}

// 描画終了
export function endDraw(vm: VM): void {
  vm.isDrawing = false
  document.body.style.cursor = ''
}

// ドロー表示トグル（UIボタン連動）
export function toggleLDraw(vm: VM): void {
  // Call the core drawing toggle function
  toggleDrawMode()

  const centerBtns = document.querySelectorAll('.center-wrapper')
  const rightBtns = document.querySelectorAll('.right-btn')
  if (vm.isSmall500) {
    if (vm.s_isDraw) {
      centerBtns.forEach((btn: any) => {
        btn.style.opacity = '0'
      })
      rightBtns.forEach((btn: any) => {
        btn.style.display = 'none'
      })
    } else {
      centerBtns.forEach((btn: any) => {
        btn.style.opacity = '1'
      })
      rightBtns.forEach((btn: any) => {
        btn.style.display = 'block'
      })
    }
  }
  vm.finishLine()
}

// ドロー固定モード
export function drawFix(vm: VM, isFix: boolean): void {
  const map = vm.$store.state.map01
  if (isFix) {
    map.dragPan.disable()
    const originalEnable = map.dragPan.enable;
    // -----------------------------------------
    map.dragPan.enable = function (...args: any[]) {
      console.trace('dragPan.enable() called');
      setTimeout(() => {
        map.dragPan.disable();
      }, 50);
      return originalEnable.apply(this, args);
    };
    // -----------------------------------------
  } else {
    // フックを解除。this.originalEnableはオンロードの先頭に書いている。
    map.dragPan.enable = vm.originalEnable
    map.dragPan.enable()
  }
  vm.$store.state.isCursorOnPanel = false
}

// ドロー固定トグル
export function toggleDrawFixMethod(vm: VM): void {
  // Call the core draw fix function
  toggleDrawFixCore(vm.originalEnable)

  // UI-specific logic
  if (vm.s_isDrawFix) {
    vm.snackbarText = '画面を固定します。もう一度クリックで解除'
    vm.snackbar = true
    vm.finishLine()
  }
}

// SIMAデータをドローに追加
export function simaAddDraw(vm: VM): void {
  const map01 = vm.$store.state.map01
  // 全てのSIMAソースを取得
  const sourceIds = new Set<string>()
  map01.getStyle().layers.forEach((layer: any) => {
    if (layer.id.includes('oh-sima-') && layer.source) {
      sourceIds.add(layer.source)
    }
  })
  // 各ソースからGeoJSONを取得して統合
  const allFeatures: any[] = []
  sourceIds.forEach((sourceId: string) => {
    const source = map01.getSource(sourceId)
    const geojson = vm.getSourceData(source)
    if (geojson && geojson.features) {
      // ポリゴンとポイントのみをドローに追加（頂点は除外）
      geojson.features.forEach((feature: any) => {
        const propType = feature.properties?.type
        const geomType = feature.geometry?.type
        // ポリゴン、またはポイント（type='point'またはgeometry.type='Point'で頂点でないもの）
        const isPolygon = geomType === 'Polygon' || geomType === 'MultiPolygon'
        const isPoint = (propType === 'point' || geomType === 'Point') && propType !== 'vertex'
        if (isPolygon || isPoint) {
          // chibanをlabelに設定
          const newFeature = JSON.parse(JSON.stringify(feature))
          if (newFeature.properties.chiban) {
            newFeature.properties.label = newFeature.properties.chiban
          }
          allFeatures.push(newFeature)
        }
      })
    }
  })
  if (allFeatures.length === 0) {
    vm.$store.dispatch('messageDialog/open', {
      id: 'global',
      title: '情報',
      contentHtml: '<p>ドローに追加できるデータがありません</p>',
      options: { icon: 'mdi-information', iconColor: 'info' }
    })
    return
  }
  const geojson = {
    type: 'FeatureCollection',
    features: allFeatures
  }
  addDraw(geojson, false, false)
  vm.s_snackbar = false
  vm.$store.dispatch('messageDialog/open', {
    id: 'global',
    title: '完了',
    contentHtml: `<p>${allFeatures.length}件のデータをドローに追加しました</p>`,
    options: { icon: 'mdi-check-circle', iconColor: 'success' }
  })
}

// ドローUIのトグル（計測レイヤー表示制御含む）
export function drawToggle(vm: VM): void {
  const map = vm.$store.state.map01
  if (!vm.showDrawUI) {
    document.querySelector('.maplibregl-ctrl-bottom-right')!.setAttribute('style', 'display:block')
    vm.drawControl.activate()
    if (vm.geojson) vm.drawInstance.addFeatures(vm.geojson);
    setTimeout(() => {
      map.setLayoutProperty('terradraw-measure-polygon-label', 'visibility', 'visible');
      map.setLayoutProperty('terradraw-measure-line-label', 'visibility', 'visible');
      map.setLayoutProperty('terradraw-measure-line-node', 'visibility', 'visible');
      map.moveLayer('terradraw-measure-polygon-label')
      map.moveLayer('terradraw-measure-line-label')
      map.moveLayer('terradraw-measure-line-node')
    },0)
    vm.showDrawUI = true
  } else {
    document.querySelector('.maplibregl-ctrl-bottom-right')!.setAttribute('style', 'display:none')
    vm.geojson = vm.drawInstance.getSnapshot()
    vm.drawControl.deactivate()
    map.setLayoutProperty('terradraw-measure-polygon-label', 'visibility', 'none');
    map.setLayoutProperty('terradraw-measure-line-label', 'visibility', 'none');
    map.setLayoutProperty('terradraw-measure-line-node', 'visibility', 'none');
    vm.showDrawUI = false
  }
}

// ドロー設定ウィンドウの表示トグル
export function drawConfig(vm: VM): void {
  const mapName = 'map01'
  // FloatingWindow用に修正
  const windowId = 'draw-config-window-' + mapName
  const currentVisible = vm.$store.state.floatingWindows[windowId]
  if (currentVisible) {
    vm.$store.dispatch('hideFloatingWindow', windowId)
  } else {
    vm.$store.dispatch('showFloatingWindow', windowId)
  }
}

// ドロー共有ウィンドウの表示トグル
export function drawShare(vm: VM): void {
  const mapName = 'map01'
  // FloatingWindow用に修正
  const windowId = 'draw-share-window-' + mapName
  const currentVisible = vm.$store.state.floatingWindows[windowId]
  if (currentVisible) {
    vm.$store.dispatch('hideFloatingWindow', windowId)
  } else {
    vm.$store.dispatch('showFloatingWindow', windowId)
  }
}
